import { useState } from 'react';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import IconButton from 'components/common/IconButton/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import ListIcon from '@mui/icons-material/List';
import ChartIcon from '@mui/icons-material/ShowChart';
import DeleteIcon from '@mui/icons-material/Delete';
import ShowIcon from '@mui/icons-material/Visibility';
import HideIcon from '@mui/icons-material/VisibilityOff';
import { DashboardPlotData } from 'components/dashboards/dashboardData';

import './GraphPlotItem.scss';

type GraphPlotItemProp = {
  plot: DashboardPlotData;
  fullPage?: boolean;
  editCallback?: () => void;
  showHideCallback?: (show: boolean) => void;
  deleteCallback?: () => void;
  exploreLogsHref?: string;
  explorePlotHref?: string;
};

/**
 * A plot component that is used in Add Graph Modal for users to edit and delete plot
 *
 * @prop plot               The plot model
 * @prop editCallback       Function to call to edit plot
 * @prop hideCallback       Function to call to hide/show a plot
 * @prop deleteCallback     Function to call to delete plot
 */
function GraphPlotItem({
  plot,
  fullPage = false,
  editCallback,
  showHideCallback,
  deleteCallback,
  exploreLogsHref,
  explorePlotHref,
}: GraphPlotItemProp): JSX.Element {

  const hasPlotData = !plot.isPQ;
  const [isPlotShown, setIsPlotShown] = useState(true);
  const togglePlot = (): void => {
    if (showHideCallback) {
      showHideCallback(!isPlotShown);
    }
    setIsPlotShown(!isPlotShown);
  };

  const handleEdit = (): void => {
    if (editCallback && hasPlotData) {
      editCallback();
    }
  };

  return (
    <>
      <div className={'GraphPlotItem' + (!plot.isPQ ? ' GraphPlotItem__Editable' : '')}>
        <div className="GraphPlotItem__ColorAndLabel" onClick={handleEdit} role="presentation">
          <div
            className={isPlotShown ? 'GraphPlotItem__PlotColor' : 'GraphPlotItem__PlotColor GraphPlotItem__PlotColor--Hidden'}
            style={isPlotShown ? { backgroundColor: `${plot.color}` } : {}}
          />
          <div>{plot.label}</div>
        </div>

        {fullPage && (
          <>
            {hasPlotData && <IconButton tooltip="Explore Logs" href={exploreLogsHref} icon={<ListIcon fontSize="small" />} />}
            {hasPlotData && <IconButton tooltip="Explore Plot" href={explorePlotHref} icon={<ChartIcon fontSize="small" />} />}
            {isPlotShown && <IconButton tooltip="Hide Plot" onClick={togglePlot} icon={<HideIcon fontSize="small" />} />}
            {!isPlotShown && <IconButton tooltip="Show Plot" onClick={togglePlot} icon={<ShowIcon fontSize="small" />} />}
            {hasPlotData && editCallback && <IconButton tooltip="Edit Plot" onClick={handleEdit} icon={<EditIcon fontSize="small" />} />}
          </>
        )}

        {!fullPage && (
          <div>
            <Button onClick={handleEdit} variant={ButtonVariant.BORDERLESS} leftIcon={<EditIcon fontSize="small" />}>Edit</Button>
            <Button onClick={deleteCallback} variant={ButtonVariant.BORDERLESS} leftIcon={<DeleteIcon fontSize="small" />}>Delete</Button>
          </div>
        )}
      </div>
    </>
  );
}

export default GraphPlotItem;
